/* ==========================================================================
   FILE: modules/brain/components/ContextSettings.jsx
   IAMONEAI - Context Settings Component
   Work/Personal/Family context configuration
   ========================================================================== */

import React, { useState, useCallback } from 'react';
import { Toggle, Button, Input, Select } from '../../../global/components';
import './ContextSettings.css';

// ==========================================================================
// DEFAULT CONTEXTS
// ==========================================================================

const DEFAULT_CONTEXTS = {
  personal: {
    name: 'Personal',
    icon: '🏠',
    tone: 'friendly',
    style: 'casual',
    enabled: true,
    color: '#10B981', // green
  },
  work: {
    name: 'Work',
    icon: '💼',
    tone: 'professional',
    style: 'formal',
    enabled: true,
    color: '#3B82F6', // blue
  },
  family: {
    name: 'Family',
    icon: '👨‍👩‍👧‍👦',
    tone: 'warm',
    style: 'caring',
    enabled: true,
    color: '#F59E0B', // orange
  },
};

// ==========================================================================
// TONE & STYLE OPTIONS
// ==========================================================================

const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'warm', label: 'Warm' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'supportive', label: 'Supportive' },
];

const STYLE_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'caring', label: 'Caring' },
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
];

const ICON_OPTIONS = [
  '🏠', '💼', '👨‍👩‍👧‍👦', '🎓', '💪', '🎨', '🎮', '✈️', '📚', '🧘', '💡', '🔬',
];

const COLOR_OPTIONS = [
  { value: '#10B981', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#84CC16', label: 'Lime' },
];

// ==========================================================================
// CONTEXT SETTINGS COMPONENT
// ==========================================================================

/**
 * Context configuration for work/personal/family modes
 * 
 * @param {Object} props
 * @param {Object} contexts - Current contexts configuration
 * @param {string} activeContext - Currently active context
 * @param {Function} onUpdate - Handler for updates
 */
function ContextSettings({
  contexts = {},
  activeContext = 'personal',
  onUpdate,
  className = '',
}) {
  const [editingContext, setEditingContext] = useState(null);
  const [newContextName, setNewContextName] = useState('');

  // Merge with defaults
  const mergedContexts = {
    ...DEFAULT_CONTEXTS,
    ...contexts,
  };

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleContextUpdate = useCallback((contextId, field, value) => {
    onUpdate?.(`contexts.${contextId}.${field}`, value);
  }, [onUpdate]);

  const handleSetActive = useCallback((contextId) => {
    onUpdate?.('activeContext', contextId);
  }, [onUpdate]);

  const handleToggleEnabled = useCallback((contextId, enabled) => {
    // Can't disable active context
    if (contextId === activeContext && !enabled) {
      return;
    }
    onUpdate?.(`contexts.${contextId}.enabled`, enabled);
  }, [activeContext, onUpdate]);

  const handleCreateContext = useCallback(() => {
    const trimmed = newContextName.trim();
    if (!trimmed) return;

    const id = trimmed.toLowerCase().replace(/\s+/g, '_');
    if (mergedContexts[id]) {
      alert('A context with this name already exists');
      return;
    }

    onUpdate?.(`contexts.${id}`, {
      name: trimmed,
      icon: '📌',
      tone: 'friendly',
      style: 'casual',
      enabled: true,
      color: '#6B7280',
      custom: true,
    });
    setNewContextName('');
  }, [newContextName, mergedContexts, onUpdate]);

  const handleDeleteContext = useCallback((contextId) => {
    if (contextId === activeContext) {
      alert('Cannot delete the active context');
      return;
    }
    if (!mergedContexts[contextId]?.custom) {
      alert('Cannot delete default contexts');
      return;
    }
    if (confirm(`Delete "${mergedContexts[contextId].name}" context?`)) {
      // Remove by setting to undefined (or implement proper delete)
      onUpdate?.(`contexts.${contextId}`, undefined);
    }
  }, [activeContext, mergedContexts, onUpdate]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const settingsClasses = [
    'context-settings',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={settingsClasses}>
      {/* Introduction */}
      <div className="context-intro">
        <div className="context-intro-icon">🔄</div>
        <div className="context-intro-content">
          <h4>Different Modes, Different You</h4>
          <p>
            Switch between contexts to change how I communicate. Each context 
            can have its own tone, style, and memories.
          </p>
        </div>
      </div>

      {/* Active Context Indicator */}
      <div className="active-context-banner">
        <span className="active-context-label">Currently Active:</span>
        <span 
          className="active-context-badge"
          style={{ backgroundColor: mergedContexts[activeContext]?.color }}
        >
          {mergedContexts[activeContext]?.icon} {mergedContexts[activeContext]?.name}
        </span>
      </div>

      {/* Context List */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Your Contexts</h3>
          <p className="settings-section-desc">
            Click to edit, toggle to enable/disable
          </p>
        </div>

        <div className="context-list">
          {Object.entries(mergedContexts).map(([contextId, context]) => (
            <ContextCard
              key={contextId}
              contextId={contextId}
              context={context}
              isActive={contextId === activeContext}
              isEditing={editingContext === contextId}
              onEdit={() => setEditingContext(editingContext === contextId ? null : contextId)}
              onSetActive={() => handleSetActive(contextId)}
              onToggleEnabled={(enabled) => handleToggleEnabled(contextId, enabled)}
              onUpdate={(field, value) => handleContextUpdate(contextId, field, value)}
              onDelete={() => handleDeleteContext(contextId)}
            />
          ))}
        </div>
      </div>

      {/* Create New Context */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Create New Context</h3>
          <p className="settings-section-desc">
            Add a custom context for specific situations
          </p>
        </div>

        <div className="create-context">
          <Input
            type="text"
            placeholder="e.g., Study, Fitness, Side Project"
            value={newContextName}
            onChange={(e) => setNewContextName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateContext();
              }
            }}
          />
          <Button
            variant="secondary"
            onClick={handleCreateContext}
            disabled={!newContextName.trim()}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Tips */}
      <div className="context-tips">
        <h4>💡 Tips</h4>
        <ul>
          <li>Say "switch to work mode" in chat to change contexts</li>
          <li>Memories can be tagged to specific contexts</li>
          <li>Each context maintains its own conversation style</li>
        </ul>
      </div>
    </div>
  );
}

// ==========================================================================
// CONTEXT CARD COMPONENT
// ==========================================================================

/**
 * Individual context card with edit functionality
 */
function ContextCard({
  contextId,
  context,
  isActive,
  isEditing,
  onEdit,
  onSetActive,
  onToggleEnabled,
  onUpdate,
  onDelete,
}) {
  return (
    <div 
      className={`context-card ${isActive ? 'context-card-active' : ''} ${!context.enabled ? 'context-card-disabled' : ''}`}
      style={{ '--context-color': context.color }}
    >
      {/* Card Header */}
      <div className="context-card-header">
        <div className="context-card-main" onClick={onEdit}>
          <span className="context-card-icon">{context.icon}</span>
          <div className="context-card-info">
            <span className="context-card-name">{context.name}</span>
            <span className="context-card-style">{context.tone} • {context.style}</span>
          </div>
        </div>

        <div className="context-card-actions">
          {isActive ? (
            <span className="context-card-active-badge">Active</span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSetActive}
              disabled={!context.enabled}
            >
              Set Active
            </Button>
          )}
          <Toggle
            checked={context.enabled}
            onChange={onToggleEnabled}
            disabled={isActive}
            aria-label={`${context.enabled ? 'Disable' : 'Enable'} ${context.name}`}
          />
        </div>
      </div>

      {/* Expanded Editor */}
      {isEditing && (
        <div className="context-card-editor">
          <div className="editor-row">
            <label>Name</label>
            <Input
              type="text"
              value={context.name}
              onChange={(e) => onUpdate('name', e.target.value)}
              disabled={!context.custom}
            />
          </div>

          <div className="editor-row">
            <label>Icon</label>
            <div className="icon-picker">
              {ICON_OPTIONS.map((icon) => (
                <button
                  key={icon}
                  className={`icon-option ${context.icon === icon ? 'icon-option-selected' : ''}`}
                  onClick={() => onUpdate('icon', icon)}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div className="editor-row">
            <label>Color</label>
            <div className="color-picker">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color.value}
                  className={`color-option ${context.color === color.value ? 'color-option-selected' : ''}`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => onUpdate('color', color.value)}
                  aria-label={color.label}
                />
              ))}
            </div>
          </div>

          <div className="editor-row">
            <label>Tone</label>
            <Select
              options={TONE_OPTIONS}
              value={context.tone}
              onChange={(value) => onUpdate('tone', value)}
            />
          </div>

          <div className="editor-row">
            <label>Style</label>
            <Select
              options={STYLE_OPTIONS}
              value={context.style}
              onChange={(value) => onUpdate('style', value)}
            />
          </div>

          {context.custom && (
            <div className="editor-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="delete-context-btn"
              >
                🗑️ Delete Context
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default ContextSettings;